import BaseButton from "@reusables/BaseButton";
import BaseInput from "@reusables/BaseInput";
import BaseModal from "@reusables/Modals/BaseModal";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Collapse } from "@mui/material";
import { selectOperation } from "@redux/features/dfs/dfsSlice";
import { performExport } from "@redux/features/dfs/thunks";
import { isDataTransferStateActive } from "@redux/features/dfs/types";
import { useMaterializeProductDraftsMutation } from "@redux/features/productDrafts/productDraftsApi";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import React, { useEffect, useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { z } from "zod";

type MaterializationForm = {
    report_email: string;
};

interface MaterializationModalProps {
    isOpen: boolean;
    selectedIds: number[];
    onClose: () => void;
    onAccepted: () => void;
}

export default function MaterializationModal({
    isOpen,
    selectedIds,
    onClose,
    onAccepted,
}: MaterializationModalProps) {
    const { t } = useTranslation("", {
        keyPrefix: "productDrafts.allProducts.modals.materialization",
    });
    const dispatch = useAppDispatch();
    const submissionPendingRef = useRef(false);
    const userEmail = useAppSelector((state) => state.auth.user?.email ?? "");
    const exportOperation = useAppSelector(selectOperation("export"));
    const isAnyExportInProgress = isDataTransferStateActive(exportOperation);

    const schema = useMemo(
        () =>
            z.object({
                report_email: z.string().email(t("validation.email")),
            }),
        [t]
    );

    const [materializeProductDrafts, { isLoading }] = useMaterializeProductDraftsMutation();
    const { control, handleSubmit, reset } = useForm<MaterializationForm>({
        resolver: zodResolver(schema),
        defaultValues: {
            report_email: userEmail,
        },
    });

    useEffect(() => {
        if (isOpen) {
            reset({ report_email: userEmail });
        }
    }, [isOpen, reset, userEmail]);

    const handleClose = () => {
        if (!isLoading && !submissionPendingRef.current) {
            onClose();
        }
    };

    const onSubmit = handleSubmit(async (data) => {
        if (submissionPendingRef.current) return;
        submissionPendingRef.current = true;

        try {
            await dispatch(
                performExport("product_draft", () =>
                    materializeProductDrafts({ ids: selectedIds, ...data }).unwrap()
                )
            );

            toast.success(t("responses.success"));
            onAccepted();
            onClose();
        } catch (error) {
            // performExport reports the API error and resets the optimistic DFS state.
            // Keeping this modal open lets the user correct the email or retry.
            console.error("Unable to start product draft materialization", error);
        } finally {
            submissionPendingRef.current = false;
        }
    });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            width={700}
            padding="56px"
            position="center"
            useCloseIcon
            keepMounted={false}
            isLoading={isLoading}
        >
            <form className="space-y-[32px]" onSubmit={onSubmit}>
                <div className="text-center text-xl font-semibold text-accent">{t("heading")}</div>

                <Alert severity="info">{t("selectedCount", { count: selectedIds.length })}</Alert>

                <Collapse in={isAnyExportInProgress}>
                    <Alert severity="warning">{t("alerts.exportInProgress")}</Alert>
                </Collapse>

                <Controller
                    control={control}
                    name="report_email"
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t("fields.reportEmail.label")}
                            placeholder={t("fields.reportEmail.placeholder")}
                            required
                        />
                    )}
                />

                <div className="grid grid-cols-2 gap-[16px]">
                    <BaseButton
                        text={t("buttons.cancel")}
                        size="md"
                        type="button"
                        primaryOutlined
                        onClick={handleClose}
                    />
                    <BaseButton
                        text={t("buttons.materialize", { count: selectedIds.length })}
                        size="md"
                        type="submit"
                        disabled={isAnyExportInProgress || selectedIds.length === 0 || isLoading}
                        loading={isLoading}
                    />
                </div>
            </form>
        </BaseModal>
    );
}
